import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LendyIcon from "../components/lendy/LendyIcon";
import AssistantAvatar from "./AssistantAvatar";
import "./agentMenu.css";

const MENU_WIDTH = 300;
const VIEWPORT_MARGIN = 12;
const ANCHOR_GAP = 6;
const MAX_MENU_HEIGHT = 420;
// Less room than this below the anchor (a phone on its side) and the menu opens above it.
const MIN_MENU_HEIGHT = 200;

/** Under the anchor (or above it when there's no room below), centred on it, kept inside the sidebar. */
const getMenuPosition = (anchor) => {
  const { top: anchorTop, bottom, left, width } = anchor.getBoundingClientRect();
  const menuWidth = Math.min(MENU_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const centredLeft = left + width / 2 - menuWidth / 2;
  const maxLeft = window.innerWidth - VIEWPORT_MARGIN - menuWidth;
  const spaceBelow = window.innerHeight - bottom - ANCHOR_GAP - VIEWPORT_MARGIN;
  const spaceAbove = anchorTop - ANCHOR_GAP - VIEWPORT_MARGIN;
  const isAbove = spaceBelow < MIN_MENU_HEIGHT && spaceAbove > spaceBelow;
  const vertical = isAbove
    ? { bottom: window.innerHeight - anchorTop + ANCHOR_GAP, maxHeight: Math.min(MAX_MENU_HEIGHT, spaceAbove) }
    : { top: bottom + ANCHOR_GAP, maxHeight: Math.min(MAX_MENU_HEIGHT, spaceBelow) };

  return { ...vertical, left: Math.max(VIEWPORT_MARGIN, Math.min(centredLeft, maxLeft)), width: menuWidth };
};

/** Switches the agent Ask Addy answers as: the agents the organization and its loan officers created. */
const AgentMenu = ({ anchor, agents, activeId, onPick, onClose }) => {
  const menuRef = useRef(null);
  // The parent passes a new onClose each render; the listeners below always call the latest.
  const onCloseRef = useRef(onClose);
  const [position, setPosition] = useState(() => getMenuPosition(anchor));

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useLayoutEffect(() => {
    setPosition(getMenuPosition(anchor));
  }, [anchor]);

  useEffect(() => {
    menuRef.current?.querySelector('[aria-checked="true"]')?.focus();
  }, []);

  useEffect(() => {
    const close = () => onCloseRef.current();
    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target) && !anchor.contains(event.target)) {
        close();
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        close();
        anchor.focus?.();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", close);
    };
  }, [anchor]);

  const renderAgent = ({ id, name, description, imageUrl }) => {
    const isActive = id === activeId;
    return (
      <button
        key={id}
        type="button"
        role="menuitemradio"
        aria-checked={isActive}
        className="agent-menu-item"
        onClick={() => onPick(id)}
      >
        {imageUrl ? (
          <AssistantAvatar imageUrl={imageUrl} size="agent" />
        ) : (
          <span className="agent-menu-initial" aria-hidden="true">
            {(name || "?").trim().charAt(0).toUpperCase()}
          </span>
        )}
        <span className="agent-menu-text">
          <b>{name}</b>
          {description && <small>{description}</small>}
        </span>
        {isActive && <LendyIcon name="check" size={16} className="agent-menu-check" />}
      </button>
    );
  };

  return createPortal(
    <div ref={menuRef} className="agent-menu" role="menu" aria-label="Switch agent" style={position}>
      <div className="agent-menu-label">Agents</div>
      <div className="agent-menu-list">{agents.map(renderAgent)}</div>
    </div>,
    document.body
  );
};

export default AgentMenu;
